// Persistent storage utility for ERP modules
// Fixes the bug where data disappears after navigation

export interface PersistentData {
  products?: any[];
  vendors?: any[];
  customers?: any[];
  employees?: any[];
  stockMovements?: any[];
  warehouses?: any[];
  purchaseOrders?: any[];
  salesOrders?: any[];
  invoices?: any[];
  leads?: any[];
  opportunities?: any[];
  campaigns?: any[];
  journalEntries?: any[];
  accounts?: any[];
  [key: string]: any;
}

const STORAGE_KEY = 'erp_persistent_data';

export class PersistentStorage {
  private static data: PersistentData = {};
  private static initialized = false;

  // Initialize data from localStorage
  static init() {
    if (!this.initialized) {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          this.data = JSON.parse(stored);
        }
        this.initialized = true;
      } catch (error) {
        console.warn('Failed to load persistent data:', error);
        this.data = {};
        this.initialized = true;
      }
    }
  }

  // Save data to localStorage
  static save() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data));
    } catch (error) {
      console.warn('Failed to save persistent data:', error);
    }
  }

  // Get data for a specific key
  static get<T>(key: string, defaultValue: T): T {
    this.init();
    return this.data[key] ?? defaultValue;
  }

  // Set data for a specific key
  static set(key: string, value: any) {
    this.init();
    this.data[key] = value;
    this.save();
  }

  // Update specific items in an array
  static updateArray<T extends { id: string }>(key: string, newItem: T, defaultArray: T[] = []) {
    this.init();
    const currentArray = this.data[key] || defaultArray;
    const existingIndex = currentArray.findIndex((item: T) => item.id === newItem.id);
    
    let updatedArray;
    if (existingIndex >= 0) {
      // Update existing item
      updatedArray = [...currentArray];
      updatedArray[existingIndex] = newItem;
    } else {
      // Add new item
      updatedArray = [...currentArray, newItem];
    }
    
    this.data[key] = updatedArray;
    this.save();
    return updatedArray;
  }

  // Add item to array
  static addToArray<T>(key: string, newItem: T, defaultArray: T[] = []) {
    this.init();
    const currentArray = this.data[key] || defaultArray;
    const updatedArray = [...currentArray, newItem];
    this.data[key] = updatedArray;
    this.save();
    return updatedArray;
  }

  // Remove item from array by id
  static removeFromArray(key: string, itemId: string, defaultArray: any[] = []) {
    this.init();
    const currentArray = this.data[key] || defaultArray;
    const updatedArray = currentArray.filter((item: any) => item.id !== itemId);
    this.data[key] = updatedArray;
    this.save();
    return updatedArray;
  }

  // Clear all data
  static clear() {
    this.data = {};
    this.save();
  }

  // Clear specific key
  static clearKey(key: string) {
    this.init();
    delete this.data[key];
    this.save();
  }

  // Initialize with mock data if empty
  static initializeWithMockData(key: string, mockData: any[]) {
    this.init();
    if (!this.data[key] || this.data[key].length === 0) {
      this.data[key] = mockData;
      this.save();
    }
    return this.data[key];
  }
} 